import { useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../components/ui/popover/index.js";
import { cn } from "../../../../lib/utils.js";

export interface WizardRadioOption {
  value: string;
  label: ReactNode;
  disabled?: boolean;
  description?: ReactNode;
}

export function WizardRadioSelect({
  value,
  onChange,
  options,
  ariaLabel,
  className,
  listClassName,
  disabled = false,
}: {
  value: string;
  onChange: (value: string) => void;
  options: readonly WizardRadioOption[];
  ariaLabel: string;
  className?: string;
  /** Optional scroll container for long option lists. */
  listClassName?: string;
  disabled?: boolean;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const selected = options.find((option) => option.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          disabled={disabled}
          className={cn(
            "flex h-10 w-full min-w-0 cursor-pointer items-center justify-between gap-2 rounded-md border border-line bg-surface px-3 text-start text-sm text-ink hover:border-line-strong disabled:cursor-not-allowed disabled:opacity-60",
            className,
          )}
        >
          <span className="min-w-0 flex-1 truncate">{selected?.label ?? value}</span>
          <ChevronDown size={16} className="shrink-0 opacity-70" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-1">
        <div
          className={listClassName}
          onWheel={(event) => event.stopPropagation()}
        >
        {options.map((option) => {
          const isSelected = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              disabled={option.disabled}
              className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-start text-sm hover:bg-surface-muted disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => {
                if (option.disabled) return;
                onChange(option.value);
                setOpen(false);
              }}
            >
              <span
                className={cn(
                  "size-4 shrink-0 rounded-full border",
                  isSelected ? "border-[4.5px] border-accent" : "border-line-strong",
                  option.disabled && "opacity-40",
                )}
                aria-hidden="true"
              />
              <span className="min-w-0 flex-1">{option.label}</span>
              {option.description ? (
                <span className="shrink-0 text-[0.65rem] text-ink-subtle">
                  {option.description}
                </span>
              ) : null}
            </button>
          );
        })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
